import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import {
  PromotionSelectionCriteria,
  type DeferredPromotionModel,
  type DeferredPromotionOption,
} from '@commerce/core'
import { YALO_PALETTE, YALO_RADIUS, YALO_SEMANTIC, YALO_SHADOW } from '@commerce/tokens'

interface Props {
  models: readonly DeferredPromotionModel[]
  /** Which promotions are in flight, so one busy card does not freeze the rest. */
  activatingUids: ReadonlySet<string>
  /** The option is the shopper's choice; absent when the server makes it. */
  onClaim: (model: DeferredPromotionModel, option?: DeferredPromotionOption) => void
}

/**
 * The sku is not a key here. `toRewards` deliberately does not dedup — a group
 * can grant the same sku twice under different reward types, and both lines are
 * real — so two thumbnails would share a key, React would keep one, and the
 * shopper would see one gift where the group grants two.
 */
const rewardThumbnails = (option: DeferredPromotionOption) =>
  option.rewards.map(
    (reward, index) =>
      reward.imageUrl && (
        <Box
          key={`${reward.sku}-${index}`}
          component="img"
          src={reward.imageUrl}
          alt=""
          sx={{
            width: 28,
            height: 28,
            flex: 'none',
            objectFit: 'contain',
            borderRadius: `${YALO_RADIUS.sm}px`,
          }}
        />
      ),
  )

/**
 * The promotions the cart has already earned and not yet claimed.
 *
 * `Cart.deferredPromotions` is the backend saying "this basket qualifies, tell
 * me which reward" — until something answers, the reward is not on the order.
 * The storefront read the field and threw it away, so the shopkeeper never saw
 * the discount their own basket had already won.
 *
 * Every control here sends one `activateDeferredPromotion` on press. A customer
 * selection is *one button per reward group* rather than a radio list and a
 * submit: the choice is the whole payload, so a two-step flow would only add a
 * state that can disagree with what is on screen.
 *
 * It renders a model and decides nothing: which promotions are claimable, what
 * each reward is called and whether a group can be chosen at all are
 * `toDeferredPromotionModels`' answers.
 */
export const DeferredPromotions = ({ models, activatingUids, onClaim }: Props) => {
  /**
   * Which option was pressed, so the picker can show the spinner on it rather
   * than only greying every option out. Local state because it is feedback
   * about this screen, not something the cart records: `activatingUids` is
   * keyed by promotion, and a promotion does not know which of its rewards the
   * shopper picked. Stale values are harmless — the spinner is only drawn while
   * that promotion is also busy.
   */
  const [pressed, setPressed] = useState<string | null>(null)

  if (models.length === 0) return null

  return (
    <Stack component="section" aria-label="Promociones que ya ganaste" spacing={1.25}>
      <Typography variant="overline" color="text.secondary">
        Promociones que ya ganaste
      </Typography>

      {models.map((model) => {
        const busy = activatingUids.has(model.promotionUid)

        return (
          <Stack
            key={model.promotionUid}
            data-testid={`deferred-promotion-${model.promotionUid}`}
            spacing={1.25}
            aria-busy={busy}
            sx={{
              bgcolor: YALO_SEMANTIC.bgSurface,
              border: `1px solid ${YALO_PALETTE.green}`,
              borderRadius: `${YALO_RADIUS.lg}px`,
              p: 1.75,
              boxShadow: YALO_SHADOW.xs,
            }}
          >
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 700 }}>{model.title}</Typography>
              {/* Only worth saying when the basket earned it more than once —
                  "×1" is noise, and a missing count is not the same as one. */}
              {model.count != null && model.count > 1 && (
                <Typography sx={{ fontSize: 12.5, flex: 'none' }} color="text.secondary">
                  ×{model.count}
                </Typography>
              )}
            </Stack>

            {model.description && (
              <Typography sx={{ fontSize: 12.5 }} color="text.secondary">
                {model.description}
              </Typography>
            )}

            {/* Named rather than reached by elimination. `=== DEFAULT_GROUP_ID
                ? single : picker` is only correct while the union has exactly
                two members: `QUANTITY_SELECTION` is expected to land later
                (see `docs/NOT_PORTED.md`), and under the old shape it would
                have rendered the "Elige una opción" heading over zero buttons,
                because the mapper leaves `options` empty for anything that is
                not a customer selection. */}
            {model.selectBy === PromotionSelectionCriteria.CUSTOMER_SELECTION ? (
              <>
                <Typography sx={{ fontSize: 12.5 }} color="text.secondary">
                  Elige una opción
                </Typography>
                {model.options.map((option) => {
                  const claiming = busy && pressed === `${model.promotionUid}::${option.identifier}`

                  return (
                    <Button
                      key={`${option.identifier}-${option.label}`}
                      fullWidth
                      disabled={busy}
                      onClick={() => {
                        setPressed(`${model.promotionUid}::${option.identifier}`)
                        onClaim(model, option)
                      }}
                      sx={{
                        justifyContent: 'flex-start',
                        minHeight: 42,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: `${YALO_RADIUS.md}px`,
                        border: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
                        color: 'text.primary',
                        fontWeight: 600,
                        fontSize: 13,
                        textAlign: 'left',
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ width: '100%', textTransform: 'none' }}
                      >
                        {rewardThumbnails(option)}
                        <Box component="span" sx={{ flex: 1 }}>
                          {option.label}
                        </Box>
                        {/* The same feedback the single control gives. Without
                            it a sighted shopper who presses an option sees the
                            buttons grey out and nothing else; `aria-busy` on
                            the card already covers a screen reader. */}
                        {claiming && <CircularProgress size={16} aria-label="Agregando" />}
                      </Stack>
                    </Button>
                  )
                })}
              </>
            ) : (
              <Button
                fullWidth
                disabled={busy}
                onClick={() => onClaim(model)}
                sx={{
                  height: 42,
                  borderRadius: `${YALO_RADIUS.pill}px`,
                  bgcolor: 'primary.light',
                  color: 'primary.dark',
                  fontWeight: 600,
                  fontSize: 13.5,
                  '&:hover': { bgcolor: 'primary.light', filter: 'brightness(0.97)' },
                }}
              >
                {busy ? (
                  <CircularProgress size={18} aria-label="Agregando" />
                ) : (
                  'Agregar a mi pedido'
                )}
              </Button>
            )}
          </Stack>
        )
      })}
    </Stack>
  )
}
